import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Plus, Trash2 } from "lucide-react";

const EMPTY_ITEM = { description: "", hsn_sac: "", quantity: 1, rate: 0 };
const EMPTY = {
  invoice_number: "", status: "Draft", issue_date: "", due_date: "",
  gst_type: "None", cgst_rate: "", sgst_rate: "", igst_rate: "",
  client_gstin: "", place_of_supply: "", billing_address: "", notes: "",
};

export default function InvoiceFormDialog({ open, onOpenChange, clientId, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(EMPTY);
      setItems([{ ...EMPTY_ITEM }]);
    }
  }, [open]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const updateItem = (idx, key, value) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [key]: value } : it)));
  };
  const addItem = () => setItems((prev) => [...prev, { ...EMPTY_ITEM }]);
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const taxable = items.reduce((sum, it) => sum + (Number(it.quantity) || 0) * (Number(it.rate) || 0), 0);
  const cgstAmt = form.gst_type === "CGST+SGST" ? (taxable * (Number(form.cgst_rate) || 0)) / 100 : 0;
  const sgstAmt = form.gst_type === "CGST+SGST" ? (taxable * (Number(form.sgst_rate) || 0)) / 100 : 0;
  const igstAmt = form.gst_type === "IGST" ? (taxable * (Number(form.igst_rate) || 0)) / 100 : 0;
  const total = taxable + cgstAmt + sgstAmt + igstAmt;

  const handleSubmit = async () => {
    const validItems = items.filter((it) => it.description.trim());
    if (!form.invoice_number || !form.issue_date || !form.due_date || validItems.length === 0) {
      toast.error("Invoice number, dates, and at least one line item are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/clients/${clientId}/invoices`, {
        invoice_number: form.invoice_number, status: form.status, issue_date: form.issue_date, due_date: form.due_date,
        line_items: validItems.map((it) => ({ description: it.description, hsn_sac: it.hsn_sac || null, quantity: Number(it.quantity) || 1, rate: Number(it.rate) || 0 })),
        gst_type: form.gst_type, cgst_rate: Number(form.cgst_rate) || 0, sgst_rate: Number(form.sgst_rate) || 0, igst_rate: Number(form.igst_rate) || 0,
        client_gstin: form.client_gstin || null, place_of_supply: form.place_of_supply || null, billing_address: form.billing_address || null,
        notes: form.notes || null,
      });
      toast.success("GST invoice created");
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invoice-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">New GST Sales Invoice</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label>Invoice Number</Label>
              <Input data-testid="invoice-form-number-input" value={form.invoice_number} onChange={set("invoice_number")} placeholder="INV-2026-004" />
            </div>
            <div className="space-y-1.5">
              <Label>Issue Date</Label>
              <Input data-testid="invoice-form-issue-date-input" type="date" value={form.issue_date} onChange={set("issue_date")} />
            </div>
            <div className="space-y-1.5">
              <Label>Due Date</Label>
              <Input data-testid="invoice-form-due-date-input" type="date" value={form.due_date} onChange={set("due_date")} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="invoice-form-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Draft">Draft</SelectItem>
                  <SelectItem value="Sent">Sent</SelectItem>
                  <SelectItem value="Paid">Paid</SelectItem>
                  <SelectItem value="Overdue">Overdue</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Client GSTIN</Label>
              <Input data-testid="invoice-form-client-gstin-input" value={form.client_gstin} onChange={set("client_gstin")} placeholder="27AAAAA0000A1Z5" />
            </div>
            <div className="space-y-1.5">
              <Label>Place of Supply</Label>
              <Input data-testid="invoice-form-place-of-supply-input" value={form.place_of_supply} onChange={set("place_of_supply")} placeholder="Maharashtra" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Billing Address</Label>
            <Textarea data-testid="invoice-form-billing-address-input" value={form.billing_address} onChange={set("billing_address")} rows={2} />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Line Items</Label>
              <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="invoice-form-add-item-button">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add Item
              </Button>
            </div>
            <div className="space-y-2" data-testid="invoice-form-line-items">
              {items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`invoice-form-line-item-${idx}`}>
                  <Input className="col-span-5" data-testid={`invoice-item-description-${idx}`} value={it.description} onChange={(e) => updateItem(idx, "description", e.target.value)} placeholder="Description" />
                  <Input className="col-span-2" data-testid={`invoice-item-hsn-${idx}`} value={it.hsn_sac} onChange={(e) => updateItem(idx, "hsn_sac", e.target.value)} placeholder="HSN/SAC" />
                  <Input className="col-span-2" data-testid={`invoice-item-qty-${idx}`} type="number" min="0" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} placeholder="Qty" />
                  <Input className="col-span-2" data-testid={`invoice-item-rate-${idx}`} type="number" min="0" value={it.rate} onChange={(e) => updateItem(idx, "rate", e.target.value)} placeholder="Rate" />
                  <button type="button" data-testid={`invoice-item-remove-${idx}`} onClick={() => removeItem(idx)} disabled={items.length === 1} className="col-span-1 text-red-600 disabled:opacity-30">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4">
            <div className="space-y-1.5">
              <Label>GST Type</Label>
              <Select value={form.gst_type} onValueChange={(v) => setForm({ ...form, gst_type: v })}>
                <SelectTrigger data-testid="invoice-form-gst-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="None">None</SelectItem>
                  <SelectItem value="CGST+SGST">CGST + SGST</SelectItem>
                  <SelectItem value="IGST">IGST</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.gst_type === "CGST+SGST" && (
              <>
                <div className="space-y-1.5">
                  <Label>CGST %</Label>
                  <Input data-testid="invoice-form-cgst-rate-input" type="number" min="0" value={form.cgst_rate} onChange={set("cgst_rate")} placeholder="9" />
                </div>
                <div className="space-y-1.5">
                  <Label>SGST %</Label>
                  <Input data-testid="invoice-form-sgst-rate-input" type="number" min="0" value={form.sgst_rate} onChange={set("sgst_rate")} placeholder="9" />
                </div>
              </>
            )}
            {form.gst_type === "IGST" && (
              <div className="space-y-1.5">
                <Label>IGST %</Label>
                <Input data-testid="invoice-form-igst-rate-input" type="number" min="0" value={form.igst_rate} onChange={set("igst_rate")} placeholder="18" />
              </div>
            )}
          </div>

          <div className="bg-[#F8F9FA] border border-[#E2E8F0] rounded-md p-3 text-sm space-y-1" data-testid="invoice-form-gst-summary">
            <div className="flex justify-between"><span className="text-[#4B5563]">Taxable Amount</span><span className="font-medium">₹{taxable.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></div>
            {form.gst_type === "CGST+SGST" && (
              <>
                <div className="flex justify-between"><span className="text-[#4B5563]">CGST</span><span>₹{cgstAmt.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></div>
                <div className="flex justify-between"><span className="text-[#4B5563]">SGST</span><span>₹{sgstAmt.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></div>
              </>
            )}
            {form.gst_type === "IGST" && (
              <div className="flex justify-between"><span className="text-[#4B5563]">IGST</span><span>₹{igstAmt.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></div>
            )}
            <div className="flex justify-between font-semibold text-[#1C1C1E] border-t border-[#E2E8F0] pt-1 mt-1">
              <span>Total</span><span data-testid="invoice-form-total-preview">₹{total.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea data-testid="invoice-form-notes-input" value={form.notes} onChange={set("notes")} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="invoice-form-cancel-button">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="invoice-form-submit-button" className="bg-[#76C121] hover:bg-[#5C9819] text-white">
            {submitting ? "Saving..." : "Create Invoice"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
